/**
 * Send a signed-in person to the two doors (`/welcome`) when their workspace
 * has no Team and no GitHub yet.
 *
 * Only on arrival: the redirect fires when the person lands on the default
 * path (`/` or the home, `/waiting`), once per page load, so a deep link or a person who
 * walks back to Work after reading the doors is never bounced. A skip is
 * remembered per workspace (`welcomeSkip.ts`), and connecting GitHub or
 * creating a Team makes the facts false, so either way the doors do not come
 * back.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { AuthSessionResponse } from "@yagni/shared";

import { AUTHENTICATED_DEFAULT_PATH } from "../routes/defaults.js";
import { hasSkippedWelcome } from "./welcomeSkip.js";

export const WELCOME_PATH = "/welcome";

/** The paths a sign-in lands on. Anything else is a place the person chose. */
const ARRIVAL_PATHS: ReadonlySet<string> = new Set(["/", AUTHENTICATED_DEFAULT_PATH]);

export interface WelcomeFacts {
  /** Any Team in the lists the reader gets back from `GET /api/desks`. */
  hasTeam: boolean;
  /** The workspace has a GitHub connection (never a personal one). */
  hasGitHub: boolean;
}

/** Whether the doors are the right first screen for this arrival. */
export function shouldOpenWelcome(input: {
  pathname: string;
  skipped: boolean;
  facts: WelcomeFacts;
}): boolean {
  if (input.skipped) return false;
  if (!ARRIVAL_PATHS.has(input.pathname)) return false;
  return !input.facts.hasTeam && !input.facts.hasGitHub;
}

type DesksList = Partial<Record<"subscribed" | "publishedByMe" | "workspaceShared" | "restricted", unknown>>;
type ServiceRow = { serviceType?: unknown; connectionScope?: unknown };

/**
 * Read both facts. A read that fails answers "has one": a person must never be
 * sent to onboarding because a request timed out.
 */
export async function readWelcomeFacts(fetchImpl: typeof fetch = fetch): Promise<WelcomeFacts> {
  const [desksRes, servicesRes] = await Promise.all([
    fetchImpl("/api/desks", { credentials: "include" }).catch(() => null),
    fetchImpl("/api/connected-services", { credentials: "include" }).catch(() => null),
  ]);
  let hasTeam = true;
  if (desksRes?.ok) {
    const body = (await desksRes.json().catch(() => null)) as DesksList | null;
    if (body && typeof body === "object") {
      hasTeam = (["subscribed", "publishedByMe", "workspaceShared", "restricted"] as const).some(
        (key) => Array.isArray(body[key]) && (body[key] as unknown[]).length > 0,
      );
    }
  }
  let hasGitHub = true;
  if (servicesRes?.ok) {
    const rows = (await servicesRes.json().catch(() => null)) as unknown;
    if (Array.isArray(rows)) {
      hasGitHub = (rows as ServiceRow[]).some(
        (row) => row?.serviceType === "github" && row.connectionScope !== "personal",
      );
    }
  }
  return { hasTeam, hasGitHub };
}

/**
 * Mount once in the signed-in App shell, with the session (null while signed
 * out or loading).
 */
export function useWelcomeRedirect(
  session: { workspace: Pick<AuthSessionResponse["workspace"], "id"> } | null,
  fetchImpl: typeof fetch = fetch,
): void {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const checked = useRef<string | null>(null);
  // The read resolves after the index route has already moved `/` to the home,
  // so the decision reads where the person is THEN, not where they arrived.
  const where = useRef(pathname);
  where.current = pathname;
  // `useNavigate` changes identity with the location outside a data router;
  // a navigation must not cancel the one read this arrival makes.
  const go = useRef(navigate);
  go.current = navigate;
  const workspaceId = session?.workspace.id ?? null;

  useEffect(() => {
    if (!workspaceId || checked.current === workspaceId) return;
    if (!ARRIVAL_PATHS.has(where.current) || hasSkippedWelcome(workspaceId)) return;
    checked.current = workspaceId;
    let cancelled = false;
    let settled = false;
    void readWelcomeFacts(fetchImpl).then((facts) => {
      settled = true;
      if (cancelled) return;
      if (
        shouldOpenWelcome({
          pathname: where.current,
          skipped: hasSkippedWelcome(workspaceId),
          facts,
        })
      ) {
        go.current(WELCOME_PATH, { replace: true });
      }
    });
    return () => {
      cancelled = true;
      // An unanswered read did not decide anything, so the next mount (React's
      // strict double effect, or a workspace switched back) asks again.
      if (!settled) checked.current = null;
    };
  }, [workspaceId, fetchImpl]);
}
